import { styled } from '@linaria/react';
import { useTranslation } from 'react-i18next';
import type { MeasureUnits } from '../../../../../api/work-periods-client/work-periods-client.type';
import {
  baseWhite,
  fontFamily,
  fontSizeSmall,
  fontWeightBold,
  fontWeightMedium,
  inkDarkest,
  skyDark,
  skyLight,
  skyLightest,
} from '../../../../../styles/design-tokens';
import { Text } from '../../../../../ui-library/typography/typography';
import { formatMeasureName } from '../../../process-analysis.helpers';
import { getUnitsLabel } from './table.helpers';
import type { MonthlyAveragesTableRow } from './table.type';

type Props = {
  groupName: string;
  rows: MonthlyAveragesTableRow[];
  entityIds: string[];
};

export const GroupSection = ({ groupName, rows, entityIds }: Props) => {
  const { t } = useTranslation();
  return (
    <GroupContainer>
      <GroupHeader>
        <GroupTitle>{groupName}</GroupTitle>
      </GroupHeader>

      <GroupTable>
        {rows.map((row, index) => (
          <TableRow key={`${groupName}-${row.measureName}-${index}`}>
            <DataCell>
              <MeasureCell>
                <Text size="small">{formatMeasureName(row.measureName, t)}</Text>
                {row.isSumType && (
                  <Text size="tiny" color={skyDark}>
                    (Total)
                  </Text>
                )}
              </MeasureCell>
            </DataCell>

            {entityIds.map((entityId) => (
              <DataCell key={entityId}>
                <ValueCell>
                  {row[entityId] !== null && row[entityId] !== undefined ? (
                    <>
                      <Text size="small">{Math.round(Number(row[entityId]))}</Text>
                      <Text size="tiny" color={skyDark}>
                        {getUnitsLabel(row.measureUnits as MeasureUnits, t)}
                      </Text>
                    </>
                  ) : (
                    <Text size="small">—</Text>
                  )}
                </ValueCell>
              </DataCell>
            ))}
          </TableRow>
        ))}
      </GroupTable>
    </GroupContainer>
  );
};

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupHeader = styled.div`
  background-color: ${skyLightest};
  border: 1px solid ${skyLight};
  border-top: none;
`;

const GroupTitle = styled.div`
  font-family: ${fontFamily};
  font-weight: ${fontWeightBold};
  font-size: 12px;
  line-height: 1.33;
  color: ${inkDarkest};
  padding: 15px 30px;
`;

const GroupTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: flex;
  background-color: ${baseWhite};
  
  &:hover {
    background-color: ${skyLightest};
  }
`;

const DataCell = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 30px;
  border-bottom: 1px solid ${skyLight};
  border-right: 1px solid ${skyLight};
  flex: 1 1 0;
  min-width: 0;
  font-family: ${fontFamily}, sans-serif;
  font-weight: ${fontWeightMedium};
  font-size: ${fontSizeSmall};
  line-height: 1.43;
  color: ${inkDarkest};
  text-align: left;
  
  &:first-child {
    border-left: 1px solid ${skyLight};
  }
  
  &:last-child {
    border-right: 1px solid ${skyLight};
  }
`;

const MeasureCell = styled.div`
  display: flex;
  align-items: end;
  gap: 4px;
`;

const ValueCell = styled.div`
  display: flex;
  align-items: end;
  gap: 4px;
`;
